import classnames from 'classnames'

import type { ICellRendererParams } from 'ag-grid-enterprise'
import type { ReactElement } from 'react'
import type { IBadgeCellRendererParams } from '~/types/badge'

import Badge from '~/components/Badge'
import Tooltip from '~/components/Tooltip'
import { MAX_TAGS_ITEM } from '~/const'

import './styles.scss'

export type DataType = 'authorization' | 'build' | 'keyword' | 'role' | 'group' | 'default'

interface Props extends ICellRendererParams<any> {
  value: IBadgeCellRendererParams
}

const BadgeCellRenderer = ({ value }: Props): ReactElement => {
  if (!value) {
    return <div className="badges-container">-</div>
  }

  const { data, maxItems, onBadgeClick, type } = value

  const wrapperClassNames = classnames('badge-cell-renderer-container', {
    'authorization-cell-renderer': type === 'authorization',
    'build-cell-renderer': type === 'build',
    'default-cell-renderer': type === 'default',
    'group-cell-renderer': type === 'group',
    'keyword-cell-renderer': type === 'keyword',
    'role-cell-renderer': type === 'role',
  })

  const counterClassNames = classnames('counter', {
    'authorization-counter': type === 'authorization',
    'build-counter': type === 'build',
    'default-counter': type === 'default',
    'group-counter': type === 'group',
    'keyword-counter': type === 'keyword',
    'role-counter': type === 'role',
  })

  const badgeClassNames = classnames('badge', {
    'authorization-badge': type === 'authorization',
    'build-badge': type === 'build',
    'default-badge': type === 'default',
    'group-badge': type === 'group',
    'keyword-badge': type === 'keyword',
    'role-badge': type === 'role',
  })

  const limit = maxItems !== undefined ? maxItems : MAX_TAGS_ITEM

  const handleBadgeClick = (id: string) => () => {
    if (onBadgeClick) onBadgeClick(id)
  }

  return (
    <div className={wrapperClassNames}>
      {data?.slice(0, limit).map(({ id, name }, index) => (
        <div key={id + index} onClick={handleBadgeClick(id)}>
          <Badge className={badgeClassNames} outlined size="medium" value={name} />
        </div>
      ))}
      {data?.length > limit ? (
        <Tooltip
          align={{ points: ['br', 'tr'] }}
          overlay={
            <div className="badge-cell-renderer-tooltip-content">
              {data?.slice(limit).map(({ id, name }) => (
                <div key={id} onClick={handleBadgeClick(id)}>
                  <Badge className={badgeClassNames} outlined value={name} />
                </div>
              ))}
            </div>
          }
          placement="top"
          trigger="hover"
        >
          <div className={counterClassNames}>+{data?.length - limit}</div>
        </Tooltip>
      ) : null}
    </div>
  )
}

export default BadgeCellRenderer
